import React, { useCallback, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import {
  DefaultButton,
  Stack,
  IconButton,
  Text,
  Shimmer,
  ShimmerElementType,
  CommandBarButton,
  TooltipHost,
  ITooltipHostStyles,
} from '@fluentui/react';
import { NoDataView } from 'common/components/DataPlaceholders';
import { EntityTagInput } from 'common/types/globalTypes';
import { dateFormat } from 'common/utils/dateFormats';
import {
  EntityTagDelete,
  EntityTagDeleteVariables,
} from 'common/graphql/__generated__/EntityTagDelete';
import { Chip } from 'common/components/Chip';
import { ToggleButton } from 'common/components/Buttons/ToggleButton';
import {
  EntityTagInvoiceSignCreate,
  EntityTagInvoiceSignCreateVariables,
} from 'common/graphql/__generated__/EntityTagInvoiceSignCreate';
import {
  GetInvoiceTags,
  GetInvoiceTagsVariables,
} from 'common/graphql/__generated__/GetInvoiceTags';
import {
  invoiceSigningAvailableTags,
  invoiceSigningAvailableTagsVariables,
} from 'common/graphql/__generated__/InvoiceSignAvailTags';
import { useStyles } from 'common/components/ActionMenu/Tags/index.styles';
import INVOICE_TAGS_LIST from '../../graphql/GetInvoiceTags.graphql';
import AVAILABLE_TAGS_LIST from '../../graphql/InvoiceSignAvailTags.graphql';
import ADD_NEW_TAG from '../../graphql/EntityTagInvoiceSignCreate.graphql';
import DELETE_TAG from '../../graphql/EntityTagDelete.graphql';
interface TagsSectionProps {
  invoiceID?: string;
  dirty?: boolean;
}
interface Tags {
  id: string;
  _rowTimestamp: string;
}
export const TransactionSigningTags: React.FC<TagsSectionProps> = ({
  invoiceID,
  dirty,
}) => {
  const styles = useStyles();

  const [toggleView, settoggleView] = useState(false);
  const [selectedTags, setSelectedTags] = React.useState<string[]>([]);
  const [addNewTag, { loading: loadingAddNewTag }] = useMutation<
    EntityTagInvoiceSignCreate,
    EntityTagInvoiceSignCreateVariables
  >(ADD_NEW_TAG);
  const [deleteTag, { loading: loadingRemoveTag }] = useMutation<
    EntityTagDelete,
    EntityTagDeleteVariables
  >(DELETE_TAG);
  const {
    data: tagsData,
    refetch: tagsListRefetch,
    loading: tagsLoading,
  } = useQuery<
    invoiceSigningAvailableTags,
    invoiceSigningAvailableTagsVariables
  >(AVAILABLE_TAGS_LIST, {
    variables: {
      id: invoiceID!,
    },
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first',
  });
  const getAvailableTags = () => {
    const variables: invoiceSigningAvailableTagsVariables = {
      id: invoiceID!,
    };
    tagsListRefetch(variables);
  };
  const getAvailableTagsMemo = useCallback(getAvailableTags, []);
  const {
    data: entityTagsData,
    refetch,
    loading: entityTagsLoading,
  } = useQuery<GetInvoiceTags, GetInvoiceTagsVariables>(INVOICE_TAGS_LIST, {
    variables: {
      id: invoiceID!,
    },
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first',
  });
  const getEntityTags = () => {
    const variables: GetInvoiceTagsVariables = {
      id: invoiceID!,
    };
    refetch(variables);
  };
  const getEntityTagsMemo = useCallback(getEntityTags, []);
  const removeTag = async (tag: Tags) => {
    await deleteTag({
      variables: {
        entityTagDeleteInput: {
          entityDelete: [
            {
              id: tag!.id,
              rowTimestamp: tag!._rowTimestamp!,
            },
          ],
        },
      },
    });
    setSelectedTags([]);
    if (!loadingRemoveTag) {
      getAvailableTagsMemo();
      getEntityTagsMemo();
    }
  };
  const AddTag = async () => {
    const tagsInputData = selectedTags.map((tag: string) => ({
      tagId: tag,
      entityId: invoiceID,
    }));
    await addNewTag({
      variables: {
        input: {
          entityTag: tagsInputData?.map(
            ({ ...tags }) => tags as EntityTagInput
          ),
        },
      },
    });
    setSelectedTags([]);
    if (!loadingAddNewTag) {
      getAvailableTagsMemo();
      getEntityTagsMemo();
    }
  };

  const disableAddtags = selectedTags.length === 0 || loadingAddNewTag;
  const calloutProps = { gapSpace: 0 };
  const hostStyles: Partial<ITooltipHostStyles> = {
    root: { display: 'inline-block' },
  };

  const isSelected = (tagId: string) =>
    selectedTags.some((item) => item === tagId);

  const onTagPressed = (tagId: string) => {
    let levelsSelected = [...selectedTags];
    let position = levelsSelected.findIndex((item) => item === tagId);
    if (position === -1) {
      levelsSelected.push(tagId);
    } else levelsSelected.splice(position, 1);
    setSelectedTags(levelsSelected);
  };

  return (
    <Stack tokens={{ childrenGap: 15 }} className={styles.tagsContainer}>
      {toggleView ? (
        <Stack tokens={{ childrenGap: 30 }} className={styles.tagsContent}>
          <Stack horizontal horizontalAlign="space-between">
            <Stack horizontal tokens={{ childrenGap: 10 }}>
              {tagsData?.invoiceSigningAvailableTags?.nodes.length === 0 &&
                !tagsLoading && (
                  <Stack.Item className={styles.emptyTagsView}>
                    <Text>No available tags to add!</Text>
                  </Stack.Item>
                )}
              <Stack
                wrap
                horizontal
                tokens={{ childrenGap: 10 }}
                className={styles.customWidth}
              >
                {tagsData?.invoiceSigningAvailableTags?.nodes &&
                  tagsData?.invoiceSigningAvailableTags?.nodes.map((tag) => {
                    return (
                      <Stack key={tag.id}>
                        <ToggleButton
                          onClick={() => onTagPressed(tag.id)}
                          toggled={isSelected(tag.id)}
                          key={tag.id}
                          text={tag.badgeName}
                          disabled={dirty}
                          iconName={isSelected(tag.id) ? 'Accept' : 'Add'}
                          color={
                            isSelected(tag.id) && tag.badgeColor
                              ? tag.badgeColor
                              : undefined
                          }
                        />
                      </Stack>
                    );
                  })}
              </Stack>
            </Stack>
            <IconButton
              onClick={() => settoggleView(false)}
              iconProps={{ iconName: 'Cancel' }}
              className={styles.closeButton}
              ariaLabel="Close"
            />
          </Stack>
          <Stack tokens={{ childrenGap: 20 }} horizontal>
            <CommandBarButton
              className={styles.commandBarButton}
              iconProps={{ iconName: 'Accept' }}
              disabled={disableAddtags}
              onClick={() => {
                AddTag();
              }}
              text={'Add tags'}
            />
            <CommandBarButton
              className={styles.commandBarButton}
              iconProps={{ iconName: 'Cancel' }}
              disabled={selectedTags.length === 0}
              onClick={() => {
                setSelectedTags([]);
              }}
              text="Cancel"
            />
          </Stack>
        </Stack>
      ) : (
        <Stack horizontal horizontalAlign="end">
          <DefaultButton
            onClick={() => settoggleView(true)}
            iconProps={{ iconName: 'Add' }}
            disabled={dirty}
            text={'Add Tags'}
          />
        </Stack>
      )}
      <>
        {entityTagsLoading && (
          <Shimmer
            shimmerElements={[
              { type: ShimmerElementType.line, width: 100, height: 40 },
            ]}
          />
        )}
        {!entityTagsLoading && (
          <Stack
            wrap
            horizontal
            tokens={{ childrenGap: 10 }}
            className={styles.customWidth}
          >
            {entityTagsData?.invoice?.entityTagsByEntityId.nodes.map(
              (entityTag) => {
                const createdDate = dateFormat(entityTag._createdDate!);
                const createdByUser = entityTag.createdByUser;
                const tooltipContent = `${createdByUser} / ${createdDate}`;
                return (
                  <TooltipHost
                    content={tooltipContent}
                    id="tooltipId"
                    calloutProps={calloutProps}
                    styles={hostStyles}
                    key={entityTag.id}
                  >
                    <Chip
                      disabled={loadingRemoveTag || dirty}
                      text={entityTag._tagBadgeName!}
                      onClick={() => {
                        removeTag({
                          id: entityTag.id || '',
                          _rowTimestamp: entityTag._rowTimestamp || '',
                        });
                      }}
                      color={entityTag._tagBadgeColor!}
                    />
                  </TooltipHost>
                );
              }
            )}
          </Stack>
        )}
        <NoDataView
          title={'No tags available!'}
          show={
            entityTagsData?.invoice?.entityTagsByEntityId.nodes.length === 0 &&
            !entityTagsLoading
          }
        />
      </>
    </Stack>
  );
};
